import { Box, Button, Flex, Select, Spinner, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';

import { TaskLogLevel, TaskLogPage } from '@app/api/tasks';
import { Txt } from '@app/components/common';
import { useApi } from '@app/hooks';

import { decodeUtf8Message } from '../utils';

const LOG_LEVEL_COLORS: Record<string, string> = {
  info: 'gray.600',
  warn: 'orange.500',
  error: 'red.500',
};

/** Порядок кнопок фильтра; неизвестные уровни из ответа добавляются в конец. */
const KNOWN_LEVELS: TaskLogLevel[] = ['error', 'warn', 'info'];

const PAGE_SIZES = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 50;

const formatDate = (value?: string) => (value ? moment(value).format('DD.MM.YYYY HH:mm:ss') : '—');

type TaskLogJournalProps = {
  /** Компонент монтируется с key={taskId}: состояние фильтра и страниц — на задачу. */
  taskId: number;
  /**
   * Меняется, когда у задачи что-то обновилось (статус, прогресс):
   * текущая страница перечитывается без спиннера.
   */
  refreshKey?: string;
};

/**
 * Журнал задачи постранично, от новых записей к старым, с фильтром по уровню.
 * Пагинация keyset: курсор страницы — id последней записи предыдущей, поэтому
 * новые записи работающей задачи не сдвигают уже открытые страницы.
 */
export const TaskLogJournal = ({ taskId, refreshKey }: TaskLogJournalProps) => {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [levels, setLevels] = useState<TaskLogLevel[]>([]);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  /** Курсоры открытых страниц: [0] — самая свежая, последний — текущая. */
  const [cursors, setCursors] = useState<number[]>([0]);
  const [page, setPage] = useState<TaskLogPage | null>(null);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);

  const cursor = cursors[cursors.length - 1];
  const pageIndex = cursors.length - 1;

  const load = useCallback(
    async (silent: boolean) => {
      const request = ++requestRef.current;
      if (!silent) {
        setLoading(true);
      }
      try {
        const response = await apiRef.current.tasks.getTaskLogs({
          id: taskId,
          levels,
          before_id: cursor || undefined,
          limit,
        });
        if (request === requestRef.current && response?.code === 200 && response.data) {
          setPage(response.data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (request === requestRef.current && !silent) {
          setLoading(false);
        }
      }
    },
    [taskId, levels, cursor, limit],
  );

  useEffect(() => {
    load(false);
  }, [load]);

  // Задача обновилась — перечитываем текущую страницу: на первой появятся
  // новые записи, на остальных обновятся счётчики, сами записи не сдвинутся.
  const firstRefresh = useRef(true);
  useEffect(() => {
    if (firstRefresh.current) {
      firstRefresh.current = false;
      return;
    }
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Смена фильтра или размера страницы — снова с самых свежих записей.
  // Курсор сбрасывается в том же рендере, чтобы не ушёл запрос со старым.
  const changeLevels = (next: TaskLogLevel[]) => {
    setCursors([0]);
    setLevels(next);
  };

  const toggleLevel = (level: TaskLogLevel) => {
    changeLevels(levels.includes(level) ? levels.filter((item) => item !== level) : [...levels, level]);
  };

  const changeLimit = (next: number) => {
    setCursors([0]);
    setLimit(next);
  };

  const counts = page?.levels || {};
  const levelOptions = [
    ...KNOWN_LEVELS,
    ...(Object.keys(counts).filter((level) => !KNOWN_LEVELS.includes(level as TaskLogLevel)) as TaskLogLevel[]),
  ];
  const allCount = Object.values(counts).reduce((sum, count) => sum + (count || 0), 0);

  const entries = page?.data || [];
  const total = page?.total || 0;
  const from = entries.length > 0 ? pageIndex * limit + 1 : 0;
  const to = entries.length > 0 ? from + entries.length - 1 : 0;
  const nextCursor = page?.next_before_id || 0;

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Txt color="gray.400" fontSize="sm">
          Лог ({total})
        </Txt>
        <Flex gap={1} alignItems="center" flexWrap="wrap">
          <Txt color="gray.400" fontSize="sm" mr={1}>
            Уровень:
          </Txt>
          <Button size="xs" variant={levels.length === 0 ? 'solid' : 'outline'} onClick={() => changeLevels([])}>
            Все ({allCount})
          </Button>
          {levelOptions.map((level) => (
            <Button
              key={level}
              size="xs"
              variant={levels.includes(level) ? 'solid' : 'outline'}
              color={levels.includes(level) ? undefined : LOG_LEVEL_COLORS[level]}
              onClick={() => toggleLevel(level)}
            >
              {level} ({counts[level] || 0})
            </Button>
          ))}
        </Flex>
      </Flex>

      {loading && !page ? (
        <Flex justifyContent="center" py={4}>
          <Spinner size="sm" />
        </Flex>
      ) : entries.length === 0 ? (
        <Txt color="gray.400">{levels.length > 0 ? 'Нет записей выбранных уровней' : 'Записей пока нет'}</Txt>
      ) : (
        <Box maxH="360px" overflowY="auto" borderWidth="1px" borderRadius="md" opacity={loading ? 0.6 : 1}>
          <Table size="sm">
            <Thead position="sticky" top={0} bg="white" zIndex={1}>
              <Tr>
                <Th>Время</Th>
                <Th>Уровень</Th>
                <Th>Элемент</Th>
                <Th>Сообщение</Th>
              </Tr>
            </Thead>
            <Tbody>
              {entries.map((entry, index) => (
                <Tr key={entry.id ?? `${entry.created_at}-${index}`}>
                  <Td whiteSpace="nowrap">{formatDate(entry.created_at)}</Td>
                  <Td>
                    <Txt color={LOG_LEVEL_COLORS[entry.level] || 'gray.500'}>{entry.level}</Txt>
                  </Td>
                  <Td>{entry.item_index < 0 ? '—' : entry.item_index}</Td>
                  <Td>
                    <Txt whiteSpace="pre-wrap" wordBreak="break-word">
                      {decodeUtf8Message(entry.message)}
                    </Txt>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {(pageIndex > 0 || nextCursor > 0 || total > PAGE_SIZES[0]) && (
        <Flex justifyContent="space-between" alignItems="center" mt={2} gap={2} flexWrap="wrap">
          <Flex alignItems="center" gap={2}>
            <Txt color="gray.500" fontSize="sm">
              На странице
            </Txt>
            <Select size="xs" w="72px" value={limit} onChange={(event) => changeLimit(Number(event.target.value))}>
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </Flex>
          <Flex alignItems="center" gap={2}>
            <Txt color="gray.500" fontSize="sm">
              {from}–{to} из {total}
            </Txt>
            <Button size="xs" variant="outline" isDisabled={pageIndex === 0 || loading} onClick={() => setCursors([0])}>
              Свежие
            </Button>
            <Button
              size="xs"
              variant="outline"
              isDisabled={pageIndex === 0 || loading}
              onClick={() => setCursors((current) => current.slice(0, -1))}
            >
              ‹ Новее
            </Button>
            <Button
              size="xs"
              variant="outline"
              isDisabled={nextCursor === 0 || loading}
              onClick={() => setCursors((current) => [...current, nextCursor])}
            >
              Старее ›
            </Button>
          </Flex>
        </Flex>
      )}
    </Box>
  );
};
